import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Group } from '../../../../model/group.model';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import { AuthService } from '../../../../services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: 'app-group',
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: './group-index.component.html',
  styleUrl: './group-index.component.scss'
})
export class GroupIndexComponent implements OnInit, OnDestroy {
  // Search fields
  public idSearch: string = '';
  public groupIdSearch: string = '';
  public portfolioUuidSearch: string = '';
  public labelSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    groupId: '',
    portfolioUuid: '',
    label: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'groupsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  protected isLoading: boolean = false;
  public isAdmin: boolean = false;
  public error: string | null = null;
  public displayedGroups: Group[] = [];

  public router: Router;

  constructor(private injector: Injector, private authService: AuthService, private groupService: ApiService){
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.checkAuthorization();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.fetchGroups();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 1,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.groupIdSearch) {
      params['group_id'] = this.groupIdSearch.trim();
    }
    if (this.portfolioUuidSearch) {
      params['division'] = this.portfolioUuidSearch.trim();
    }
    if (this.labelSearch) {
      params['label'] = this.labelSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      groupId: this.groupIdSearch,
      portfolioUuid: this.portfolioUuidSearch,
      label: this.labelSearch,
      updatedBy: this.updatedBySearch
    });
  }

  fetchGroups() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.groupService.getGroups(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedGroups = response;
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading groups:', err);
          this.error = extractErrorMessage(err, "Failed to load groups.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.fetchGroups();
  }

  /**
   * Navigation helper
   */
  nextPage(groupId: any, route: string, router: Router) {
    goToNextPage(groupId, route, router);
  }

  /**
   * Delete group
   */
  delete(group: any) {
    if (confirm('Are you sure you want to delete this group?')) {
      this.groupService.deleteGroup(group.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.fetchGroups(); // Reload the list after deletion
          },
          error: (err) => {
            console.error('Error deleting group:', err);
            this.error = extractErrorMessage(err, "Failed to delete group.");
          }
        });
    }
  }

  /**
   * TrackBy function for *ngFor optimization
   */
  trackByGroupId(index: number, group: any): number {
    return group.id;
  }
}
